import type { Screenshot } from 'types';
import { useState } from 'react';
import { useScreenshot } from '@/state/experiments/useScreenshot';
import { ScreenshotItem } from './ScreenshotItem';

type ScreenshotListProps = { variantId: number; experimentId: number };

export const ScreenshotList = ({ variantId, experimentId }: ScreenshotListProps) => {
  const { screenshots, isLoading, deleteScreenshot, updateScreenshot } = useScreenshot({ variantId, experimentId });
  const [editing, setEditing] = useState<number | null>(null);
  const [commentValue, setCommentValue] = useState<string>('');

  const onSave = async () => {
    if (editing && commentValue) {
      await updateScreenshot(editing, commentValue);
    }
    setEditing(null);
    setCommentValue('');
  };

  const onDelete = async (screenshotId: number) => {
    await deleteScreenshot(screenshotId);
  };

  if (isLoading) {
    return <p className="supporting mt-4 animate-pulse">Loading screenshots…</p>;
  }

  if (!screenshots || screenshots.length === 0) {
    return <p className="supporting mt-4">No screenshots for this variant yet. Capture them from the visual editor.</p>;
  }

  return (
    <div className="mt-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
        {/*
          The card reveals its own controls from CSS now, so there is no `hovered`
          state to thread through here. Driving it from `onMouseEnter` meant the
          edit/download/delete buttons only existed while a mouse pointer was over
          the card: no tab stop could ever reach them, and on touch there is no
          hover at all, so a screenshot could not be annotated or deleted.
        */}
        {screenshots.map((screenshot: Screenshot) => (
          <ScreenshotItem
            key={screenshot.id}
            screenshot={screenshot}
            onSave={onSave}
            onDelete={onDelete}
            editing={editing}
            setEditing={setEditing}
            setCommentValue={setCommentValue}
          />
        ))}
      </div>
    </div>
  );
};
